/** biome-ignore-all lint/style/useNamingConvention: camel case */
import {memo, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {GoogleAdBanner} from '~/common/fabric/GoogleAds/GoogleAdBanner';
import {
  selectEventOptionEnablePhotoSharing,
  selectIsHostViewing,
} from '~/common/fabric/reducers/eviteEvent/selectors';
import {selectPhotosEnabled} from '~/common/fabric/reducers/giftingOptions/selectors';
import {selectIsHost} from '~/common/fabric/User/reducers/user';
import {useGetAdsQuery} from '~/ViewInvitation/api/endpoints/content';
import {usePhotos} from '~/ViewInvitation/hooks/usePhotos';
import {DetailsModule} from '~/ViewInvitation/routes/Landing/DetailsModule';
import {DonationsModule} from '~/ViewInvitation/routes/Landing/DonationModule/DonationsModule';
import {GiftingModule} from '~/ViewInvitation/routes/Landing/GiftingModule/GiftingModule';
import {HostPhotosModule} from '~/ViewInvitation/routes/Landing/HostPhotosModule';
import {Module} from '~/ViewInvitation/routes/Landing/Module';
import {PhotosModule} from '~/ViewInvitation/routes/Landing/PhotosModule/PhotosModule';
import {PollModule} from '~/ViewInvitation/routes/Landing/PollModule/PollModule';
import {RegistryModule} from '~/ViewInvitation/routes/Landing/RegistryModule';
import {SettingsModule} from '~/ViewInvitation/routes/Landing/SettingsModule/SettingsModule';
import {TitleModule} from '~/ViewInvitation/routes/Landing/TitleModule';
import {WhatToBringModule} from '~/ViewInvitation/routes/Landing/WhatToBringModule';
import {WhosComingModule} from '~/ViewInvitation/routes/Landing/WhosComingModule';
import {
  selectHostUploadProcessing,
  selectSharedUploadProcessing,
} from '~/ViewInvitation/selectors/photos';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';

import './Modules.sass';

const keysToComponentMap = {
  event_title: TitleModule,
  event_details: DetailsModule,
  who_is_coming: WhosComingModule,
  gifting: GiftingModule,
  donation: DonationsModule,
  poll: PollModule,
  what_to_bring: WhatToBringModule,
  photo: PhotosModule,
  host_photo: HostPhotosModule,
  settings: SettingsModule,
  gift_registry: RegistryModule,
};

const getComponentFromKey = (moduleName) => {
  if (typeof keysToComponentMap[moduleName] !== 'undefined') {
    const Component = keysToComponentMap[moduleName];
    return <Component />;
  }
  return null;
};

const MemoizedGoogleAdBanner = memo(GoogleAdBanner);
const MemoizedModule = memo(Module);

export const Modules = ({
  googleAdsKey,
  adsPageType,
  displayOrder,
  preview = false,
  ctasToForceShow,
  readonly = true,
  disableContent = false,
  hideGoogleAd = false,
  openPhotoGallery = false,
  openPhotoViewer = false,
  onRemoveAdsClick = () => {},
}) => {
  const isDesktop = useIsLargishAndUp();
  const eventId = useSelector((state) => state.eviteEvent?.id);
  const isHostViewing = useSelector(selectIsHostViewing);
  const isHost = useSelector(selectIsHost);
  const isPremium = useSelector((store) => store.eventDetails?.isPremium);
  const hostPhotosEnabled = useSelector(
    (state) => state.eviteEvent?.options.event_option_enable_host_photo_gallery
  );
  const hostUploadProcessing = useSelector(selectHostUploadProcessing);
  const sharedUploadProcessing = useSelector(selectSharedUploadProcessing);
  const isCreationFlow = !window.location.pathname.startsWith('/event');
  const photosEnabledCreateFlow = useSelector(selectPhotosEnabled);
  const photosEnabledNotCreateFlow = useSelector(selectEventOptionEnablePhotoSharing);
  const photosEnabled = isCreationFlow ? photosEnabledCreateFlow : photosEnabledNotCreateFlow;
  const shouldShowAdditionalAds = !isPremium && !hideGoogleAd;
  const searchParams = new URLSearchParams(window.location.search);
  const isGuestPreview = !!searchParams.get('preview');

  const adTarget = isHost || isHostViewing ? 'host' : 'guest';
  useGetAdsQuery(eventId, {
    skip: !shouldShowAdditionalAds,
    selectFromResult: ({data}) => ({
      firstAffiliateAd: data?.ad_before?.[adTarget],
      secondAffiliateAd: data?.ad_after?.[adTarget],
    }),
  });

  if (displayOrder.indexOf('second_ad') === -1) {
    displayOrder.splice(1, 0, 'first_ad');
    displayOrder.push('second_ad');
  }

  const TrackableModules = {
    what_to_bring: 'wtbImpression',
  };

  const {photos: hostPhotos} = usePhotos({
    eventId,
    isHostModule: true,
    perPage: 1,
  });

  const hideHostModule = (!isHost && !hostPhotos?.length) || !hostPhotosEnabled;
  const modulesWithEditableSubmodules = useMemo(() => {
    const modules = ['gifting'];
    if (isDesktop) modules.push('event_title', 'event_details', 'donation');
    return modules;
  }, [isDesktop]);
  const modulesWithRemoveButtons = ['photo'];
  const staticModules = ['who_is_coming'];

  return displayOrder.map((moduleName, index) => {
    let processing = false;
    const isLast = index === displayOrder.length - 1;
    const module = getComponentFromKey(moduleName);

    if (moduleName === 'gift_registry' && displayOrder.includes('gifting')) return null;
    if (moduleName === 'host_photo' && !isCreationFlow && hideHostModule) return null;
    if (moduleName === 'photo' && !photosEnabled && (!preview || isGuestPreview || isPremium)) {
      return null;
    }
    if (moduleName === 'host_photo') {
      processing = hostUploadProcessing;
    }
    if (moduleName === 'photo') {
      processing = sharedUploadProcessing;
    }
    if (['first_ad', 'second_ad'].includes(moduleName)) {
      const adIndex = moduleName === 'first_ad' ? 1 : 2;
      const adLocation = moduleName === 'first_ad' ? 'mid' : 'bot';
      if (!shouldShowAdditionalAds) return null;
      return (
        <MemoizedGoogleAdBanner
          key={`${moduleName}-${googleAdsKey}`}
          index={adIndex}
          isCreateEditPreview={preview}
          isGuestPreview={isGuestPreview}
          removeOnClick={onRemoveAdsClick}
          showRemoveButton={isHost || isHostViewing}
          location={adLocation}
          pageType={adsPageType}
        />
      );
    }

    const highlightOnHover =
      isDesktop &&
      preview &&
      !readonly &&
      ![...modulesWithEditableSubmodules, ...modulesWithRemoveButtons, ...staticModules].includes(
        moduleName
      );

    return module
      ? <MemoizedModule
          key={moduleName}
          moduleName={moduleName}
          isLast={isLast}
          impressionEvent={TrackableModules[moduleName]}
          preview={preview}
          readonly={readonly}
          disableContent={disableContent}
          forceShowCTA={ctasToForceShow.includes(moduleName)}
          highlightOnHover={highlightOnHover}
          openPhotoGallery={openPhotoGallery}
          openPhotoViewer={openPhotoViewer}
          processing={processing}
          isPremiumEvent={isPremium}
        >
          {module}
        </MemoizedModule>
      : null;
  });
};
